import React from "react";
import { Box, alpha, useTheme } from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { lightTheme } from "../../styles/theme";
import { getAttachmentDisplayName } from "../../utils/noticeCardDisplay";

export type NoticeCardMediaAttachment = {
  name: string;
  url: string;
  type: string;
};

type Props = {
  attachment: NoticeCardMediaAttachment;
  isVideo: boolean;
  onOpen: () => void;
  size?: number;
};

const DEFAULT_SIZE = 72;

const NoticeCardMedia: React.FC<Props> = ({
  attachment,
  isVideo,
  onOpen,
  size = DEFAULT_SIZE,
}) => {
  const theme = useTheme();
  const label = getAttachmentDisplayName(attachment.name);

  return (
    <Box
      component="button"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      aria-label={isVideo ? `Play ${label}` : `View ${label}`}
      title={label}
      sx={{
        flexShrink: 0,
        width: size,
        height: size,
        position: "relative",
        p: 0,
        border: "1px solid",
        borderColor: lightTheme.colors.borderSlateLight,
        borderRadius: 1.5,
        overflow: "hidden",
        cursor: "pointer",
        bgcolor: isVideo
          ? lightTheme.colors.surfaceTealMist
          : lightTheme.colors.borderSlateLight,
        transition: "border-color 0.15s ease",
        "&:hover": {
          borderColor: lightTheme.colors.primary,
        },
        "&:hover .notice-card-media-overlay": {
          opacity: isVideo ? 1 : 0.35,
        },
      }}
    >
      {isVideo ? (
        <Box
          className="notice-card-media-overlay"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
          }}
        >
          <PlayCircleOutlineIcon
            sx={{ fontSize: size * 0.4, opacity: 0.9 }}
          />
        </Box>
      ) : (
        <>
          <Box
            component="img"
            src={attachment.url}
            alt={label}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
          <Box
            className="notice-card-media-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: alpha(theme.palette.common.black, 0.2),
              opacity: 0,
              transition: "opacity 0.15s ease",
            }}
          />
        </>
      )}
    </Box>
  );
};

export default NoticeCardMedia;
